import {
    Amount,
    ApiResponse,
    ChargemasterApi,
    ClaimApi, ClaimGuarantorResponsibility,
    ClearinghousePayerApi,
    CodingApi,
    CollectionApi,
    CoreFetch,
    createGroupRequestFilters,
    createRequestFilters,
    CurrencyType,
    DocumentApi,
    EligibilityApi, EmailApi,
    FacilityApi,
    FaxApi,
    FilterMethod,
    GuarantorApi,
    HL7Api,
    ICustomFilter,
    InstitutionalApi,
    Invoice,
    InvoiceApi,
    InvoiceStatusType,
    InvoiceType,
    LetterApi,
    ListProviderClaimsRequest,
    ListProviderGuarantorsRequest,
    ListProviderLettersRequest,
    ListProviderMailCartsRequest,
    ListProviderMailRequest,
    ListProviderPatientsRequest,
    ListProviderPayersRequest, ListProviderPhoneRequest,
    ListProviderProvidersRequest,
    ListProviderReceiveFaxesRequest,
    ListProviderSellersRequest,
    ListProviderSendFaxesRequest,
    ListProviderSubscribersRequest,
    ListSearchProviderClaimsRequest,
    MailApi,
    Nynf3Api,
    ObjectNoResponse,
    PatientApi,
    PayerApi,
    PayerSubscriberApi,
    PaymentApi,
    PaymentPlanIntervalType, PhoneApi,
    ProfessionalApi,
    ProviderApi,
    ProviderChargemasterItem,
    ProviderClaim,
    ProviderClaimEligibility,
    ProviderClaimPayment,
    ProviderClaimTransmission,
    ProviderClearinghousePayer,
    ProviderCoding,
    ProviderCollectionPayment,
    ProviderCreateCollectionPaymentPayload,
    ProviderDisplayDocumentType,
    ProviderDocument, ProviderDocumentTranscription,
    ProviderDocumentUrl,
    ProviderFacility,
    ProviderGuarantor,
    ProviderInstitutionalClaim,
    ProviderInstitutionalClaimTemplate,
    ProviderLetter,
    ProviderMail,
    ProviderMailCart,
    ProviderNynf3Claim,
    ProviderNynf3ClaimTemplate,
    ProviderPatient,
    ProviderPayer,
    ProviderPayerSubscriber,
    ProviderPaymentPlan, ProviderPhone,
    ProviderProfessionalClaim,
    ProviderProfessionalClaimTemplate,
    ProviderProvider,
    ProviderReceiveFax,
    ProviderSeller, ProviderSendEmail,
    ProviderSendFax,
    ProviderStatus,
    ProviderSubscriber,
    ProviderSubscriberPatient,
    ProviderType,
    SellerApi,
    StatusApi,
    SubscriberApi,
    SubscriberPatientApi,
    SupportCheckoutCharge,
    SupportCreditCard,
    SupportHL7Message,
    SupportRefundCollectionPaymentPayload,
    SupportShortUrl,
    TemporaryDocumentUrl,
    Transaction,
    TransmissionApi, ViewProviderCMS1450DocumentRequest,
    ViewProviderCMS1500DocumentRequest,
} from '@enter/core';
import _ from 'lodash';
import { IPayerListing } from './claim.store';
import { ICommunication, IItemListing, ListProviderCommunicationsRequest } from './root.store';
import {
    betweenDayBoundsFilter,
    equalsFilter,
    fetch,
    fetchAll,
    firstAndLastFilter,
    handleError,
    isDate,
    isDateTime,
    isNumber,
    isUuid,
    likeFilter,
    listAll,
    parseFetchListRaw,
    parseResponse,
    stream2string,
} from './util';

export class ClaimService {
    public static async getByIds(ids: string[]): Promise<ProviderClaim[]> {
        const filters: ICustomFilter[][] = [
            ids.map(id => ({
                filterId: 'filterId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            })),
        ];

        const reqFilters = createGroupRequestFilters(filters);
        return CoreFetch<ClaimApi>(ClaimApi).listProviderClaims({
            ...reqFilters,
        });
    }

    public static async getAllClaimInvoices(id: string): Promise<Invoice[]> {
        const offsetStep = 50;
        const limit = 50;
        let offset = 0;
        let result: Invoice[] = [];
        let resTotal = 0;
        const filters: ICustomFilter[] = [
            {
                filterId: 'filterClaimId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            },
            {
                filterId: 'filterInvoiceType',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: InvoiceType.PATIENT,
            },
            {
                filterId: 'filterInvoiceStatus',
                filterMethod: FilterMethod.NQ,
                filterType: 'String',
                value: InvoiceStatusType.PAID,
            },
            {
                filterId: 'filterAmount',
                filterMethod: FilterMethod.GT,
                filterType: 'String',
                value: '0',
            },
        ];

        const req = createRequestFilters(filters);
        do {
            const res = await CoreFetch<InvoiceApi>(InvoiceApi).listProviderInvoicesRaw({
                ...req,
                offset,
                limit,
            });
            const { value, total } = await parseFetchListRaw(res);
            result = [...result, ...value];
            resTotal = total;
            offset += offsetStep;
        } while (result.length < resTotal);

        return result;
    }

    public static async fetchPaymentsByClaimIds(ids: string[]): Promise<ProviderClaimPayment[]> {
        if (_.isEmpty(ids)) {
            return [];
        }

        const filters: ICustomFilter[][] = [
            ids.map(id => ({
                filterId: 'filterClaimId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            })),
        ];

        const reqFilters = createGroupRequestFilters(filters);
        return CoreFetch<PaymentApi>(PaymentApi).listProviderClaimPayments({
            ...reqFilters,
        });
    }

    public static async fetchCollectionsByClaimIds(ids: string[]): Promise<ProviderCollectionPayment[]> {
        if (_.isEmpty(ids)) {
            return [];
        }

        const filters: ICustomFilter[][] = [
            ids.map(id => ({
                filterId: 'filterClaimId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            })),
        ];

        const reqFilters = createGroupRequestFilters(filters);
        return CoreFetch<CollectionApi>(CollectionApi).listProviderCollectionPayments({
            ...reqFilters,
        });
    }

    /**
     * Codings for several claims.
     *
     * `/provider/coding` is paged, so a single request answers with a slice — and a coding missing
     * from that slice reads as a rule that never ran on the claim, which is how a batch action would
     * silently offer the wrong set of rules. Claim ids are chunked for the same reason the coder
     * rules dashboard chunks them: one request cannot carry an unbounded filter list.
     */
    public static async fetchCodingsByClaimIds(ids: string[]): Promise<ProviderCoding[]> {
        if (_.isEmpty(ids)) {
            return [];
        }

        const claimIdsPerRequest = 25;
        const pageLimit = 100;
        // A full page is the only thing that keeps the walk going, so a server that ignored `offset`
        // would page forever. 25 claims never carry 2,000 codings between them, so this bounds the
        // runaway without bounding real data.
        const maxPagesPerChunk = 20;
        const codings: ProviderCoding[] = [];

        for (const claimIdChunk of _.chunk(_.uniq(ids), claimIdsPerRequest)) {
            let offset = 0;
            for (let page = 0; page < maxPagesPerChunk; page += 1) {
                const raw = await CoreFetch<CodingApi>(CodingApi).listProviderCodingsRaw({
                    // One `EQ:` value per claim, which the client serializes as repeated
                    // `filterClaimId` params. Handing `equalsFilter` the whole array instead would
                    // comma-join the ids into a single value — a different query.
                    filterClaimId: _.flatMap(claimIdChunk, id => equalsFilter(id) ?? []),
                    limit: pageLimit,
                    offset,
                });
                const { value, total } = await parseFetchListRaw<ProviderCoding>(raw);
                codings.push(...value);

                if (value.length < pageLimit) {
                    break;
                }
                offset += pageLimit;
                // `total` is `Number(headers.get('pagination-total'))`, and `Number(null)` is 0 — an
                // absent header arrives as a finite zero, not NaN. Reading that as a real total would
                // end the walk after one full page and drop every later coding, so only a positive
                // total is treated as a bound; otherwise the short page above is the end of the list.
                if (Number.isFinite(total) && total > 0 && offset >= total) {
                    break;
                }
            }
        }

        return codings;
    }

    public static async getAllClaimDocuments(ids: string[]): Promise<ProviderDocument[]> {
        // const rootGenerator = new RootGenerator(new RootStore());
        // return [rootGenerator.messageGenerator.generateProviderDocument('test')]
        const offsetStep = 50;
        const limit = 50;
        let offset = 0;
        let result: ProviderDocument[] = [];
        let resTotal = 0;
        const filters: ICustomFilter[][] = [
            ids.map(id => ({
                filterId: 'filterClaimId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            })),
        ];

        const req = createGroupRequestFilters(filters);
        do {
            const guarantorClaimsRaw = await CoreFetch<DocumentApi>(DocumentApi).listProviderDocumentsRaw({
                ...req,
                offset,
                limit,
            });
            const { value, total } = await parseFetchListRaw(guarantorClaimsRaw);
            result = [...result, ...value];
            resTotal = total;
            offset += offsetStep;
        } while (result.length < resTotal);

        return result;
    }

    public fetchClaimListing = async (
        request: ListProviderClaimsRequest,
    ): Promise<IItemListing<ProviderClaim> | undefined> => {
        const res = await fetch(request, req => CoreFetch<ClaimApi>(ClaimApi).listProviderClaimsRaw(req));
        if (res) {
            return {
                items: await parseResponse(res),
                limit: Number(await res.raw.headers.get('pagination-limit')),
                offset: Number(await res.raw.headers.get('pagination-offset')),
                total: Number(await res.raw.headers.get('pagination-total')),
            };
        }
    };

    public fetchClaim = async (claimId: string): Promise<ProviderClaim | undefined> => {
        try {
            return this.fetchClaimById(claimId);
        } catch (err) {
            throw new Error(`Invalid claim ID: ${claimId}`);
        }
    };

    public fetchBasicSearchResults = async (term: string): Promise<ProviderClaim[]> => {
       const searchTerm = _.trim(term);
       return this.fetchClaimSearch({search: [searchTerm]});
    };

    public oldFetchBasicSearchResults = async (term: string): Promise<ProviderClaim[]> => {
        const searchTerm = _.trim(term);
        const claimRequests = this.claimBasicSearchRequests(searchTerm);
        return this.fetchClaims(claimRequests);
    };

    public fetchMailListing = async (
        lpmr: ListProviderMailRequest,
    ): Promise<IItemListing<ProviderMail> | undefined> => {
        const res = await fetch(lpmr, req => CoreFetch<MailApi>(MailApi).listProviderMailRaw(req));
        if (res) {
            return {
                items: await parseResponse(res),
                limit: Number(await res.raw.headers.get('pagination-limit')),
                offset: Number(await res.raw.headers.get('pagination-offset')),
                total: Number(await res.raw.headers.get('pagination-total')),
            };
        }
    };

    public fetchReceiveFaxes = async (
        req: ListProviderReceiveFaxesRequest,
    ): Promise<IItemListing<ProviderReceiveFax> | undefined> => {
        const res = await fetch(req, r => CoreFetch<FaxApi>(FaxApi).listProviderReceiveFaxesRaw(r));
        if (res) {
            return {
                items: await parseResponse(res),
                limit: Number(await res.raw.headers.get('pagination-limit')),
                offset: Number(await res.raw.headers.get('pagination-offset')),
                total: Number(await res.raw.headers.get('pagination-total')),
            };
        }
    };

    public fetchPhones = async (
        req: ListProviderPhoneRequest,
    ): Promise<IItemListing<ProviderPhone> | undefined> => {
        const res = await fetch(req, r => CoreFetch<PhoneApi>(PhoneApi).listProviderPhoneRaw(r));
        if(res) {
            return {
                items: await parseResponse(res),
                limit: Number(await res.raw.headers.get('pagination-limit')),
                offset: Number(await res.raw.headers.get('pagination-offset')),
                total: Number(await res.raw.headers.get('pagination-total')),
            }
        }
    }

    public fetchSendFaxes = async (
        req: ListProviderSendFaxesRequest,
    ): Promise<IItemListing<ProviderSendFax> | undefined> => {
        const res = await fetch(req, r => CoreFetch<FaxApi>(FaxApi).listProviderSendFaxesRaw(r));
        if (res) {
            return {
                items: await parseResponse(res),
                limit: Number(await res.raw.headers.get('pagination-limit')),
                offset: Number(await res.raw.headers.get('pagination-offset')),
                total: Number(await res.raw.headers.get('pagination-total')),
            };
        }
    };

    public fetchCommunicationListing = async (
        lpmr: ListProviderCommunicationsRequest,
    ): Promise<IItemListing<ICommunication> | undefined> => {
        const res = await fetch(
            lpmr,
            req => CoreFetch<MailApi>(MailApi).listProviderMailRaw(req) as Promise<ApiResponse<ICommunication[]>>,
        );
        if (res) {
            return {
                items: await parseResponse(res),
                limit: Number(await res.raw.headers.get('pagination-limit')),
                offset: Number(await res.raw.headers.get('pagination-offset')),
                total: Number(await res.raw.headers.get('pagination-total')),
            };
        }
    };

    public fetchLetterListing = async (
        lpmr: ListProviderLettersRequest,
    ): Promise<IItemListing<ProviderLetter> | undefined> => {
        const res = await fetch(lpmr, req => CoreFetch<LetterApi>(LetterApi).listProviderLettersRaw(req));
        if (res) {
            return {
                items: await parseResponse(res),
                limit: Number(await res.raw.headers.get('pagination-limit')),
                offset: Number(await res.raw.headers.get('pagination-offset')),
                total: Number(await res.raw.headers.get('pagination-total')),
            };
        }
    };

    public fetchCartListing = async (
        lpmr: ListProviderMailCartsRequest,
    ): Promise<IItemListing<ProviderMailCart> | undefined> => {
        const res = await fetch(lpmr, req => CoreFetch<MailApi>(MailApi).listProviderMailCartsRaw(req));
        if (res) {
            return {
                items: await parseResponse(res),
                limit: Number(await res.raw.headers.get('pagination-limit')),
                offset: Number(await res.raw.headers.get('pagination-offset')),
                total: Number(await res.raw.headers.get('pagination-total')),
            };
        }
    };

    public fetchTemplateChargemasterItems = async (
        chargemasterId?: string,
        chargemasterItemIds?: string[],
    ): Promise<ProviderChargemasterItem[] | undefined> => {
        if (chargemasterId && chargemasterItemIds && chargemasterItemIds.length > 0) {
            // const filter = {
            //     filterId: _.map(chargemasterItemIds, id => `EQ:${id}`),
            // };

            return fetch({}, () =>
                CoreFetch<ChargemasterApi>(ChargemasterApi).listProviderChargemasterItems({
                    id: chargemasterId,
                    filterId: equalsFilter(chargemasterItemIds),
                    filterActive: `EQ:1`,
                }),
            );
        }
    };

    public fetchClaimById = async (claimId: string): Promise<ProviderClaim | undefined> => {
        return fetch(claimId, (id: string) => CoreFetch<ClaimApi>(ClaimApi).getProviderClaim({ id }));
    };
    public fetchClaims = async (reqs: ListProviderClaimsRequest[]): Promise<ProviderClaim[]> => {
        const listClaims = (req: ListProviderClaimsRequest): Promise<ProviderClaim[]> =>
            CoreFetch<ClaimApi>(ClaimApi).listProviderClaims(req);
        const claims = await listAll<ProviderClaim[]>(reqs, listClaims);
        return _.uniqBy(_.compact(_.flatten(claims)), 'id');
    };

    public fetchClaimSearch = async (req: ListSearchProviderClaimsRequest): Promise<ProviderClaim[]> => {
        const claims = await CoreFetch<ClaimApi>(ClaimApi).listSearchProviderClaims(req);
        return _.uniqBy(_.compact(claims), 'id');
    };

    public fetchProfessionalClaimByClaimId = async (
        claimId: string,
    ): Promise<ProviderProfessionalClaim | undefined> => {
        const claims = await fetch(claimId, (id: string) =>
            CoreFetch<ProfessionalApi>(ProfessionalApi).listProviderProfessionalClaims(this.filterByClaimId(id)),
        );
        return _.first(claims);
    };
    public fetchProfessionalTemplateById = async (
        _id: string,
    ): Promise<ProviderProfessionalClaimTemplate | undefined> => {
        return fetch(_id, (id: string) =>
            CoreFetch<ProfessionalApi>(ProfessionalApi).getProviderProfessionalClaimTemplate({ id }),
        );
    };

    public fetchInstitutionalClaimByClaimId = async (
        claimId: string,
    ): Promise<ProviderInstitutionalClaim | undefined> => {
        const claims = await fetch(claimId, (id: string) =>
            CoreFetch<InstitutionalApi>(InstitutionalApi).listProviderInstitutionalClaims(this.filterByClaimId(id)),
        );
        return _.first(claims);
    };
    public fetchInstitutionalTemplateById = async (
        _id: string,
    ): Promise<ProviderInstitutionalClaimTemplate | undefined> => {
        return fetch(_id, (id: string) =>
            CoreFetch<InstitutionalApi>(InstitutionalApi).getProviderInstitutionalClaimTemplate({ id }),
        );
    };

    public fetchNynf3ClaimByClaimId = async (claimId: string): Promise<ProviderNynf3Claim | undefined> => {
        const claims = await fetch(claimId, (id: string) =>
            CoreFetch<Nynf3Api>(Nynf3Api).listProviderNynf3Claims(this.filterByClaimId(id)),
        );
        return _.first(claims);
    };
    public fetchNynf3TemplateById = async (_id: string): Promise<ProviderNynf3ClaimTemplate | undefined> => {
        return fetch(_id, (id: string) => CoreFetch<Nynf3Api>(Nynf3Api).getProviderNynf3ClaimTemplate({ id }));
    };

    public fetchFacilityById = async (facilityId: string | undefined): Promise<ProviderFacility | undefined> => {
        return fetch(facilityId, (id: string) => CoreFetch<FacilityApi>(FacilityApi).getProviderFacility({ id }));
    };

    public static queryFacilities = async (query: string): Promise<ProviderFacility[] | undefined> => {
        if (query.length === 0) {
            return fetch({}, req => CoreFetch<FacilityApi>(FacilityApi).listProviderFacilities(req));
        } else {
            return fetch({ filterName: likeFilter(query) }, req =>
                CoreFetch<FacilityApi>(FacilityApi).listProviderFacilities(req),
            );
        }
    };

    public fetchGuarantorById = async (guarantorId: string | undefined): Promise<ProviderGuarantor | undefined> => {
        return fetch(guarantorId, (id: string) => CoreFetch<GuarantorApi>(GuarantorApi).getProviderGuarantor({ id }));
    };

    public static fetchGuarantors = async (reqs: ListProviderGuarantorsRequest[]): Promise<ProviderGuarantor[]> => {
        const listGuarantors = (req: ListProviderGuarantorsRequest): Promise<ProviderGuarantor[]> =>
            CoreFetch<GuarantorApi>(GuarantorApi).listProviderGuarantors(req);
        const guarantors = await listAll<ProviderGuarantor[]>(reqs, listGuarantors);
        return _.uniqBy(_.compact(_.flatten(guarantors)), 'id');
    };

    public static queryGuarantors = async (query: string): Promise<ProviderGuarantor[] | undefined> => {
        if (query.length === 0) {
            return fetch({}, req => CoreFetch<GuarantorApi>(GuarantorApi).listProviderGuarantors(req));
        } else {
            return ClaimService.fetchGuarantors(firstAndLastFilter(query));
        }
    };

    public fetchCheckoutChargesByGuarantorId = async (
        guarantorId: string | undefined,
    ): Promise<SupportCheckoutCharge[] | undefined> => {
        if (!guarantorId) {
            return [];
        }
        return [];
    };
    public fetchCreditCardsByGuarantorId = async (
        guarantorId: string | undefined,
    ): Promise<SupportCreditCard[] | undefined> => {
        if (!guarantorId) {
            return [];
        }
        return [];
    };
    public fetchPaymentPlansByGuarantorId = async (
        _guarantorId: string | undefined,
    ): Promise<ProviderPaymentPlan[] | undefined> => {
        return [];
    };

    public fetchPayerSubscriberById = async (_id: string): Promise<ProviderPayerSubscriber | undefined> => {
        return fetch(_id, (id: string) =>
            CoreFetch<PayerSubscriberApi>(PayerSubscriberApi).getProviderPayerSubscriber({ id }),
        );
    };

    public fetchClearinghousePayerById = async (_id: string): Promise<ProviderClearinghousePayer | undefined> => {
        return fetch(_id, (id: string) =>
            CoreFetch<ClearinghousePayerApi>(ClearinghousePayerApi).getProviderClearinghousePayer({ id }),
        );
    };
    public fetchPayerSubscribersById = async (
        ids?: (string | undefined)[],
    ): Promise<ProviderPayerSubscriber[] | undefined> => {
        return fetchAll<ProviderPayerSubscriber>(ids, this.fetchPayerSubscriberById);
    };

    public static queryPayerSubscribers = async (query: string): Promise<ProviderPayerSubscriber[] | undefined> => {
        if (query.length === 0) {
            return fetch({}, req => CoreFetch<PayerSubscriberApi>(PayerSubscriberApi).listProviderPayerSubscribers(req));
        } else {
            return fetch({ filterMemberId: likeFilter(query) }, req =>
                CoreFetch<PayerSubscriberApi>(PayerSubscriberApi).listProviderPayerSubscribers(req),
            );
        }
    };

    public fetchEmailsByClaimId = async (_claimId: string): Promise<ProviderSendEmail[] | undefined> => {
        try {
            return CoreFetch<EmailApi>(EmailApi).listProviderSendEmails({
                filterClaimId: equalsFilter(_claimId),
            });
        } catch (err) {
            await handleError(err);
            throw err;
        }
    };

    public fetchProviderById = async (_id: string): Promise<ProviderProvider | undefined> => {
        return fetch(_id, (id: string) => CoreFetch<ProviderApi>(ProviderApi).getProviderProvider({ id }));
    };
    public fetchProvidersById = async (ids?: (string | undefined)[]): Promise<ProviderProvider[] | undefined> => {
        return fetchAll<ProviderProvider>(ids, this.fetchProviderById);
    };
    public static fetchProviders = async (reqs: ListProviderProvidersRequest[]): Promise<ProviderProvider[]> => {
        const listProviders = (req: ListProviderProvidersRequest): Promise<ProviderProvider[]> =>
            CoreFetch<ProviderApi>(ProviderApi).listProviderProviders(req);
        const providers = await listAll<ProviderProvider[]>(reqs, listProviders);
        return _.uniqBy(_.compact(_.flatten(providers)), 'id');
    };
    public fetchBillingProviders = async (): Promise<ProviderProvider[] | undefined> => {
        try {
            return CoreFetch<ProviderApi>(ProviderApi).listProviderProviders({
                filterProviderType: equalsFilter(ProviderType.BILLING),
            });
        } catch (err) {
            await handleError(err);
            throw err;
        }
    };
    public static queryProviders = async (query: string): Promise<ProviderProvider[] | undefined> => {
        if (query.length === 0) {
            return fetch({}, req => CoreFetch<ProviderApi>(ProviderApi).listProviderProviders(req));
        } else {
            return ClaimService.fetchProviders(firstAndLastFilter(query));
        }
    };

    public fetchSubscriberPatientById = async (_id: string): Promise<ProviderSubscriberPatient | undefined> => {
        return fetch(_id, (id: string) =>
            CoreFetch<SubscriberPatientApi>(SubscriberPatientApi).getProviderSubscriberPatient({ id }),
        );
    };
    public fetchSubscriberPatientsById = async (
        ids?: (string | undefined)[],
    ): Promise<ProviderSubscriberPatient[] | undefined> => {
        return fetchAll<ProviderSubscriberPatient>(ids, this.fetchSubscriberPatientById);
    };

    public fetchSellerById = async (_id: string | undefined): Promise<ProviderSeller | undefined> => {
        return fetch(_id, (id: string) => CoreFetch<SellerApi>(SellerApi).getProviderSeller({ id }));
    };
    public fetchSellersById = async (ids?: (string | undefined)[]): Promise<ProviderSeller[] | undefined> => {
        return fetchAll<ProviderSeller>(ids, this.fetchSellerById);
    };
    public static fetchSellers = async (reqs: ListProviderSellersRequest[]): Promise<ProviderSeller[]> => {
        const listSellers = (req: ListProviderSellersRequest): Promise<ProviderSeller[]> =>
            CoreFetch<SellerApi>(SellerApi).listProviderSellers(req);
        const patients = await listAll<ProviderSeller[]>(reqs, listSellers);
        return _.uniqBy(_.compact(_.flatten(patients)), 'id');
    };
    public static querySellers = async (query: string): Promise<ProviderSeller[] | undefined> => {
        if (query.length === 0) {
            return fetch({}, req => CoreFetch<SellerApi>(SellerApi).listProviderSellers(req));
        } else {
            return ClaimService.fetchSellers(firstAndLastFilter(query));
        }
    };

    public fetchPayerById = async (_id: string): Promise<ProviderPayer | undefined> => {
        return fetch(_id, (id: string) => CoreFetch<PayerApi>(PayerApi).getProviderPayer({ id }));
    };
    public fetchClaimBundle = async (_bundleId: string): Promise<ProviderClaim[]> => {
        const claims = await fetch(_bundleId, (bundleId: string) =>
            CoreFetch<ClaimApi>(ClaimApi).listProviderClaims({
                filterClaimBundleId: equalsFilter(bundleId),
            }),
        );

        return claims || [];
    };
    public fetchPayersById = async (ids?: (string | undefined)[]): Promise<ProviderPayer[] | undefined> => {
        return fetchAll<ProviderPayer>(ids, this.fetchPayerById);
    };
    public fetchPayers = async (reqs: ListProviderPayersRequest[]): Promise<ProviderPayer[]> => {
        const listPayers = (req: ListProviderPayersRequest): Promise<ProviderPayer[]> =>
            CoreFetch<PayerApi>(PayerApi).listProviderPayers(req);
        const payers = await listAll<ProviderPayer[]>(reqs, listPayers);
        return _.uniqBy(_.compact(_.flatten(payers)), 'id');
    };
    public static queryPayers = async (query: string): Promise<ProviderPayer[] | undefined> => {
        const request = {
            filterName: query.length > 0 ? likeFilter(query) : undefined,
        };
        return fetch(request, req => CoreFetch<PayerApi>(PayerApi).listProviderPayers(req));
    };

    public fetchPayerListing = async (request: ListProviderPayersRequest): Promise<IPayerListing | undefined> => {
        const res = await fetch(request, req => CoreFetch<PayerApi>(PayerApi).listProviderPayersRaw(req));
        if (res) {
            return {
                payers: await parseResponse(res),
                limit: Number(await res.raw.headers.get('pagination-limit')),
                offset: Number(await res.raw.headers.get('pagination-offset')),
                total: Number(await res.raw.headers.get('pagination-total')),
            };
        }
    };

    public fetchSubscriberById = async (_id: string): Promise<ProviderSubscriber | undefined> => {
        return fetch(_id, (id: string) => CoreFetch<SubscriberApi>(SubscriberApi).getProviderSubscriber({ id }));
    };

    public viewLetterById = async (_id: string) => {
        return fetch(_id, (id: string) => CoreFetch<LetterApi>(LetterApi).viewProviderLetter({ id }));
    };

    public viewFaxById = async (_id: string) => {
        return fetch(_id, (id: string) => CoreFetch<FaxApi>(FaxApi).viewProviderSendFax({ id }));
    };

    public fetchSubscribersById = async (ids?: (string | undefined)[]): Promise<ProviderSubscriber[] | undefined> => {
        return fetchAll<ProviderSubscriber>(ids, this.fetchSubscriberById);
    };
    public static fetchSubscribers = async (reqs: ListProviderSubscribersRequest[]): Promise<ProviderSubscriber[]> => {
        const listSubscribers = (req: ListProviderSubscribersRequest): Promise<ProviderSubscriber[]> =>
            CoreFetch<SubscriberApi>(SubscriberApi).listProviderSubscribers(req);
        const payers = await listAll<ProviderSubscriber[]>(reqs, listSubscribers);
        return _.uniqBy(_.compact(_.flatten(payers)), 'id');
    };
    public static querySubscribers = async (query: string): Promise<ProviderSubscriber[] | undefined> => {
        if (query.length === 0) {
            return fetch({}, req => CoreFetch<SubscriberApi>(SubscriberApi).listProviderSubscribers(req));
        } else {
            return ClaimService.fetchSubscribers(firstAndLastFilter(query));
        }
    };

    public static queryPayments = async (query: string): Promise<ProviderClaimPayment[] | undefined> => {
        const trimmedQuery = _.trim(query);
        if (trimmedQuery.length === 0) {
            return [];
        }

        return fetch(
            { filterTotalAmount: likeFilter(trimmedQuery) },
            req => CoreFetch<PaymentApi>(PaymentApi).listProviderClaimPayments(req),
        );
    };

    public static queryPayerSubscribersByMemberId = async (query: string): Promise<ProviderPayerSubscriber[] | undefined> => {
        const filters: ICustomFilter[] = [
            {
                filterId: 'filterMemberId',
                filterMethod: FilterMethod.LK,
                filterType: 'String',
                value: query,
            },
        ];
        const reqFilters = createRequestFilters(filters);
        return fetch(reqFilters, req => CoreFetch<PayerSubscriberApi>(PayerSubscriberApi).listProviderPayerSubscribers(req));
    };

    public fetchPatientById = async (_id: string): Promise<ProviderPatient | undefined> => {
        return fetch(_id, (id: string) => CoreFetch<PatientApi>(PatientApi).getProviderPatient({ id }));
    };
    public fetchPatientsById = async (ids?: (string | undefined)[]): Promise<ProviderPatient[] | undefined> => {
        return fetchAll<ProviderPatient>(ids, this.fetchPatientById);
    };
    public static fetchPatients = async (reqs: ListProviderPatientsRequest[]): Promise<ProviderPatient[]> => {
        const listPatients = (req: ListProviderPatientsRequest): Promise<ProviderPatient[]> =>
            CoreFetch<PatientApi>(PatientApi).listProviderPatients(req);
        const patients = await listAll<ProviderPatient[]>(reqs, listPatients);
        return _.uniqBy(_.compact(_.flatten(patients)), 'id');
    };
    public static queryPatients = async (query: string): Promise<ProviderPatient[] | undefined> => {
        if (query.length === 0) {
            return fetch({}, req => CoreFetch<PatientApi>(PatientApi).listProviderPatients(req));
        } else {
            return ClaimService.fetchPatients(firstAndLastFilter(query));
        }
    };

    public fetchCodingsByClaimId = async (claimId: string): Promise<ProviderCoding[] | undefined> => {
        // Same `/provider/coding` list the batch path walks. A single unpaged call answers with one
        // slice, and a coding missing from that slice reads here as a rule that never ran — the
        // claim-detail chip then omits a suppressible rule. Pagination lives on the batch helper.
        return fetch(claimId, (id: string) => ClaimService.fetchCodingsByClaimIds([id]));
    };

    public fetchEligibilitiesByClaimId = async (claimId: string): Promise<ProviderClaimEligibility[] | undefined> => {
        return fetch(claimId, (id: string) =>
            CoreFetch<EligibilityApi>(EligibilityApi).listProviderClaimEligibilities(this.filterByClaimId(id)),
        );
    };

    public fetchInvoicesByClaimId = async (claimId: string): Promise<Invoice[] | undefined> => {
        return fetch(claimId, (id: string) =>
            CoreFetch<InvoiceApi>(InvoiceApi).listProviderInvoices(this.filterByClaimId(id)),
        );
    };

    public fetchLettersByClaimId = async (claimId: string): Promise<ProviderLetter[] | undefined> => {
        return fetch(claimId, (id: string) =>
            CoreFetch<LetterApi>(LetterApi).listProviderLetters(this.filterByClaimId(id)),
        );
    };

    public fetchMailByClaimId = async (claimId: string): Promise<ProviderMail[] | undefined> => {
        return fetch(claimId, (id: string) => CoreFetch<MailApi>(MailApi).listProviderMail(this.filterByClaimId(id)));
    };

    public fetchPaymentsByClaimId = async (claimId: string): Promise<ProviderClaimPayment[] | undefined> => {
        return fetch(claimId, (id: string) =>
            CoreFetch<PaymentApi>(PaymentApi).listProviderClaimPayments(this.filterByClaimId(id)),
        );
    };

    public fetchReceiveFaxesByClaimId = async (claimId: string): Promise<ProviderReceiveFax[] | undefined> => {
        return fetch(claimId, (id: string) =>
            CoreFetch<FaxApi>(FaxApi).listProviderReceiveFaxes(this.filterByClaimId(id)),
        );
    };

    public fetchSendFaxesByClaimId = async (claimId: string): Promise<ProviderSendFax[] | undefined> => {

        const maxIterations = 10;

        const tempSendFaxes: ProviderSendFax[] = [];

        let offset = 0;
        let previousLength = 0;

        for(let i=0; i < maxIterations; i++) {
            const tempSendFaxesChunk = await fetch(claimId, (id: string) =>
                CoreFetch<FaxApi>(FaxApi).listProviderSendFaxes(this.filterByClaimIdLimitOffset(id, 10, offset)),
            )
            if(tempSendFaxesChunk) {
                tempSendFaxes.push(...tempSendFaxesChunk);
            }
            if(tempSendFaxes.length > previousLength && tempSendFaxes.length > 0 && tempSendFaxes.length % 10 == 0) {
                offset+= 10;
                previousLength = tempSendFaxes.length;
            } else {
                break;
            }
        }

        return tempSendFaxes;
    };

    // FIXME: pending API change, do not request ClaimStatus
    public fetchStatusesByClaimId = async (_id: string): Promise<ProviderStatus[] | undefined> => {
        return fetch(_id, (id: string) =>
            CoreFetch<StatusApi>(StatusApi).listProviderStatuses(this.filterByClaimId(id)),
        );
    };

    public fetchTransmissionsById = async (_id: string): Promise<ProviderClaimTransmission[] | undefined> => {
        return fetch(_id, (id: string) =>
            CoreFetch<TransmissionApi>(TransmissionApi).listProviderClaimTransmissions(this.filterByClaimId(id)),
        );
    };

    public fetchPhonesById = async (_id: string): Promise<ProviderPhone[] | undefined> => {
        return fetch(_id, (id: string) =>
            CoreFetch<PhoneApi>(PhoneApi).listProviderPhone(this.filterByClaimId(id)),
        );
    }

    public fetchHl7ById = async (_id: string): Promise<SupportHL7Message[] | undefined> => {
        return fetch(_id, (id: string) => CoreFetch<HL7Api>(HL7Api).listProviderHL7Messages(this.filterByClaimId(id)));
    };

    public fetchCollectionsByClaimId = async (claimId: string): Promise<ProviderCollectionPayment[] | undefined> => {
        return fetch(claimId, (id: string) =>
            CoreFetch<CollectionApi>(CollectionApi).listProviderCollectionPayments(this.filterByClaimId(id)),
        );
    };

    public fetchEmails = async(claimId: string): Promise<ProviderSendEmail[] | undefined> => {
        return fetch(claimId, (id: string) =>
            CoreFetch<EmailApi>(EmailApi).listProviderSendEmails(this.filterByClaimId(id)),
        );
    }

    // FIXME: for now, manually parse Document responses to remove null values
    public fetchDocumentsByClaimId = async (claimId: string): Promise<ProviderDocument[] | undefined> => {
        return fetch(claimId, async (id: string) => {
            const response = await CoreFetch<DocumentApi>(DocumentApi).listProviderDocumentsRaw(
                this.filterByClaimId(id),
            );
            const results = await stream2string(response.raw.body as ReadableStream<any>);
            let models = JSON.parse(results) as ProviderDocument[];
            if(models) {
                models = models.filter(d=>d.documentType !== 'PHONE');
            }
            return _.compact(models);
        });
    };
    public fetchDocumentById = async (documentId: string): Promise<ProviderDocument | undefined> => {
        return fetch(documentId, (id: string) => CoreFetch<DocumentApi>(DocumentApi).getProviderDocument({ id }));
    };
    public fetchDocumentUrlByDocumentId = async (documentId: string): Promise<ProviderDocumentUrl | undefined> => {
        return fetch(documentId, (id: string) => CoreFetch<DocumentApi>(DocumentApi).getProviderDocumentUrl({ id }));
    };

    public fetchDocumentTranscriptByDocumentId = async (documentId: string): Promise<ProviderDocumentTranscription | undefined> => {
        return fetch(documentId, (id: string) => CoreFetch<DocumentApi>(DocumentApi).getProviderDocumentTranscription({ id }));
    }

    public fetchDocumentUrlByPaymentId = async (paymentId: string): Promise<TemporaryDocumentUrl | undefined> => {
        return fetch(paymentId, (id: string) =>
            CoreFetch<DocumentApi>(DocumentApi).viewProviderPaymentExplanationOfBenefitsDocument({ id }),
        );
    };

    public static viewCMS1500 = async (
        request: ViewProviderCMS1500DocumentRequest,
    ): Promise<TemporaryDocumentUrl | undefined> => {
        return fetch(request, (request: ViewProviderCMS1500DocumentRequest) =>
            CoreFetch<DocumentApi>(DocumentApi).viewProviderCMS1500Document(request),
        );
    };

    public static viewCMS1450 = async (
        request: ViewProviderCMS1450DocumentRequest,
    ): Promise<TemporaryDocumentUrl | undefined> => {
        return fetch(request, (request: ViewProviderCMS1450DocumentRequest) =>
            CoreFetch<DocumentApi>(DocumentApi).viewProviderCMS1450Document(request),
        );
    };


    public static refreshDocumentMetadata = async (documentId: string): Promise<ObjectNoResponse | undefined> => {
        return fetch(documentId, (id: string) =>
            CoreFetch<DocumentApi>(DocumentApi).refreshProviderDocumentMetadata({ id, body: {} }),
        );
    };
    public fetchDocumentUrlsByDocumentIds = async (
        documentIds: string[],
    ): Promise<ProviderDocumentUrl[] | undefined> => {
        return fetchAll<ProviderDocumentUrl>(documentIds, this.fetchDocumentUrlByDocumentId);
    };
    public fetchMailCartById = async (mailCartId: string): Promise<ProviderMailCart | undefined> => {
        return fetch(mailCartId, (id: string) => CoreFetch<MailApi>(MailApi).getProviderMailCart({ id }));
    };
    public fetchTransactionsByGroupIds = async (_groupIds: string[]): Promise<Transaction[] | undefined> => {
        return [];
    };
    public fetchPaymentPlansByInvoiceIds = async (_invoiceIds: string[]): Promise<ProviderPaymentPlan[] | undefined> => {
        return [];
    };
    public fetchPaymentPlansByClaimId = async (_claimId: string): Promise<ProviderPaymentPlan[] | undefined> => {
        return [];
    };

    public addInvoiceLineItem = async (
        invoiceId: string,
        name: string,
        amount: Amount,
    ): Promise<Invoice | undefined> => {
        return fetch(invoiceId, (id: string) =>
            CoreFetch<InvoiceApi>(InvoiceApi).postProviderInvoiceItem({
                id,
                createBillingInvoiceItemPayload: {
                    name,
                    amount,
                },
            }),
        );
    };
    public addInvoicePaymentPlan = async (
        _invoiceId: string,
        _amountPerCycle: number,
        _intervalCount: number,
        _intervalSpan: PaymentPlanIntervalType,
    ): Promise<ProviderPaymentPlan | undefined> => {
        return undefined;
    };
    public disableInvoicePaymentPlan = async (_paymentPlanId: string): Promise<ProviderPaymentPlan | undefined> => {
        return undefined;
    };

    public fetchInvoiceCheckoutUrl = async (_invoiceId: string): Promise<SupportShortUrl | undefined> => {
        return undefined;
    };
    public fetchGuarantorCheckoutUrl = async (_guarantorId: string): Promise<SupportShortUrl | undefined> => {
        return undefined;
    };
    public fetchGuarantorClaims = async (guarantorId: string): Promise<ProviderClaim[] | undefined> => {
        return fetch(guarantorId, (id: string) =>
            CoreFetch<ClaimApi>(ClaimApi).listProviderClaims({ filterGuarantorId: equalsFilter(guarantorId) }),
        );
    };
    public fetchGuarantorViewBillUrl = async (_guarantorId: string): Promise<TemporaryDocumentUrl | undefined> => {
        return undefined;
    };
    public fetchGuarantorBillingDocuments = async (
        claimId: string,
        guarantorId: string,
    ): Promise<ProviderDocument[] | undefined> => {
        try {
            const letters = await CoreFetch<LetterApi>(LetterApi).listProviderLetters(this.filterByClaimId(claimId));
            const documents = _.compact(_.map(letters, 'document'));
            return _.filter(documents, {
                documentType: ProviderDisplayDocumentType.GUARANTORBILL,
            });
        } catch (err) {
            handleError(err);
        }
    };

    public refundCheckoutCharge = async (
        checkoutCharge: SupportCheckoutCharge,
        supportRefundCollectionPaymentPayload: SupportRefundCollectionPaymentPayload,
    ): Promise<SupportCheckoutCharge | undefined> => {
        if (checkoutCharge && checkoutCharge.id) {
            return fetch(checkoutCharge.id, (id: string) =>
                CoreFetch<CollectionApi>(CollectionApi).postProviderCollectionPaymentRefund({
                    id,
                    providerRefundCollectionPaymentPayload: supportRefundCollectionPaymentPayload,
                }),
            );
        }
    };

    public refundCollectionPayment = async (
        paymentId: string,
        supportRefundCollectionPaymentPayload: SupportRefundCollectionPaymentPayload,
    ): Promise<ProviderCollectionPayment | undefined> => {
        return fetch(paymentId, (id: string) =>
            CoreFetch<CollectionApi>(CollectionApi).postProviderCollectionPaymentRefund({
                id,
                providerRefundCollectionPaymentPayload: supportRefundCollectionPaymentPayload,
            }),
        );
    };

    public createCollectionPayment = async (
        providerCreateCollectionPaymentPayload: ProviderCreateCollectionPaymentPayload,
    ): Promise<ProviderCollectionPayment | undefined> => {
        return CoreFetch<CollectionApi>(CollectionApi).postProviderCreateCollectionPayment({
            providerCreateCollectionPaymentPayload,
        });
    };

    public updateClaimGuarantorResponsibility = async (
        claimGuarantorResponsibility: ClaimGuarantorResponsibility,
        claimId: string
    ): Promise<ObjectNoResponse | undefined> => {
        return CoreFetch<ClaimApi>(ClaimApi).postProviderClaimGuarantorResponsibility({
            id: claimId,
            claimGuarantorResponsibility: claimGuarantorResponsibility
        });
    }

    private filterByClaimId = (id: string): object => {
        return {
            filterClaimId: equalsFilter(id),
        };
    };

    private filterByClaimIdLimitOffset = (id: string, limit: number, offset: number): object => {
        return {
            filterClaimId: equalsFilter(id),
            limit,
            offset
        };
    };

    private readonly claimBasicSearchRequests = (term: string): ListProviderClaimsRequest[] => {
        const requests = [];
        if (isDateTime(term) || isDate(term)) {
            const filter = isDateTime(term) ? betweenDayBoundsFilter(term) : equalsFilter(term);
            requests.push({ filterPaymentDate: filter });
            requests.push({ filterFromDos: filter });
        } else if (isNumber(term) && _.size(term) > 5) {
            requests.push({ filterInvoiceNumber: equalsFilter(term) });
        } else if (isUuid(term)) {
            const filter = equalsFilter(term);
            requests.push({ filterId: filter });
            requests.push({ filterFacilityId: filter });
            requests.push({ filterGuarantorId: filter });
            requests.push({ filterPatientId: filter });
            requests.push({ filterPayerId: filter });
            requests.push({ filterProviderId: filter });
            requests.push({ filterSubscriberId: filter });
        } else {
            // string
            const filter = likeFilter(term);
            requests.push({ filterFirst: filter });
            requests.push({ filterLast: filter });
        }
        return requests;
    };
}
